import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Code,
  Divider,
  Group,
  Loader,
  Menu,
  Modal,
  NumberInput,
  Paper,
  Select,
  Stack,
  Tabs,
  Text,
  Textarea,
  TextInput,
  Timeline,
  Title,
  Tooltip,
} from "@mantine/core";
import { SafeDateInput as DateInput } from "@/components/SafeDateInput";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  IconArrowLeft,
  IconChevronDown,
  IconCopy,
  IconPrinter,
  IconFileTypeDocx,
  IconMail,
  IconDeviceFloppy,
  IconLock,
  IconLockOpen,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import { api } from "@/lib/api";
import { formatAPIError, qk } from "@/lib/apiHelpers";
import { Can } from "@/components/Can";
import { LoadingPointsTab } from "./LoadingPointsTab";
import { CargoTab } from "./CargoTab";
import { ServicesTab } from "./ServicesTab";
import { PaymentsTab } from "./PaymentsTab";
import { DocumentsTab } from "./DocumentsTab";
import { SendPrintDialog } from "./SendPrintDialog";
import type {
  AuditEntry,
  AuditListResponse,
  CarriersListResponse,
  ClientsListResponse,
  CompaniesListResponse,
  Contract,
  Order,
  OrderCreateRequest,
  OrderUpdateRequest,
  OrderStatus,
  UsersListResponse,
  VehiclesListResponse,
} from "@/types/api";

// ─── helpers ────────────────────────────────────────────────────────────────

function today(): Date {
  return new Date();
}

function parseDate(s?: string): Date | null {
  if (!s) return null;
  const d = dayjs(s);
  return d.isValid() ? d.toDate() : null;
}

function formatMoney(n: number): string {
  return n.toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " ₽";
}

// ─── audit helpers ───────────────────────────────────────────────────────────

const ACTION_COLORS: Record<string, string> = {
  create: "green",
  update: "blue",
  delete: "red",
  status_change: "teal",
  unlock: "orange",
  login: "teal",
  logout: "gray",
  login_failed: "orange",
  password_change: "violet",
  password_reset: "violet",
  permissions_set: "indigo",
};

const ACTION_LABELS: Record<string, string> = {
  create: "Создание",
  update: "Изменение",
  delete: "Удаление",
  status_change: "Смена статуса",
  unlock: "Снятие блокировки",
};

function actionLabel(action: string): string {
  return ACTION_LABELS[action] ?? action;
}

// ─── form shape ─────────────────────────────────────────────────────────────

interface FormValues {
  company_id: string;
  order_type: string;
  document_date: Date | null;
  number_external: string;
  title: string;
  responsible_manager_id: string;
  // client section
  client_id: string;
  client_contract_id: string;
  client_amount: number | string;
  client_vat_rate: number | string;
  // carrier section
  carrier_id: string;
  carrier_contract_id: string;
  carrier_amount: number | string;
  carrier_vat_rate: number | string;
  // transport
  vehicle_id: string;
  vehicle_text: string;
  driver_id: string;
  driver_text: string;
  distance_km: number | string;
  // extra
  notes: string;
  extra_conditions: string;
  additional_terms_client: string;
  additional_terms_carrier: string;
}

// ─── AuditTimeline component ─────────────────────────────────────────────────

interface AuditTimelineProps {
  orderId: string;
  active: boolean;
}

function AuditTimeline({ orderId, active }: AuditTimelineProps) {
  const { data, isLoading } = useQuery({
    queryKey: qk.orderAudit(orderId),
    queryFn: async () => {
      const params = new URLSearchParams({
        entity_type: "order",
        entity_id: orderId,
        page: "1",
        page_size: "100",
      });
      return (await api.get<AuditListResponse>(`/api/v1/audit?${params.toString()}`)).data;
    },
    enabled: active,
    staleTime: 0,
  });

  if (isLoading) {
    return (
      <Stack align="center" pt="xl">
        <Loader size="sm" />
        <Text c="dimmed" size="sm">Загрузка истории…</Text>
      </Stack>
    );
  }

  const entries: AuditEntry[] = data?.items ?? [];

  if (entries.length === 0) {
    return (
      <Paper p="xl" withBorder>
        <Text c="dimmed" ta="center" size="sm">Действий пока нет.</Text>
      </Paper>
    );
  }

  // Sort newest first
  const sorted = [...entries].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );

  return (
    <Paper p="md" withBorder>
      <Timeline active={sorted.length - 1} bulletSize={20} lineWidth={2}>
        {sorted.map((entry) => (
          <Timeline.Item
            key={entry.id}
            bullet={
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: "white" }} />
            }
            color={ACTION_COLORS[entry.action] ?? "gray"}
          >
            <Group gap="xs" mb={4} wrap="wrap">
              <Badge color={ACTION_COLORS[entry.action] ?? "gray"} variant="light" size="sm">
                {actionLabel(entry.action)}
              </Badge>
              {entry.user_login && (
                <Text size="sm" fw={500}>{entry.user_login}</Text>
              )}
              <Text size="xs" c="dimmed">
                {new Date(entry.created_at).toLocaleString("ru-RU")}
              </Text>
            </Group>
            {entry.changes && Object.keys(entry.changes).length > 0 && (
              <Code block style={{ fontSize: 11, maxWidth: 480, marginTop: 4 }}>
                {JSON.stringify(entry.changes, null, 2)}
              </Code>
            )}
          </Timeline.Item>
        ))}
      </Timeline>
    </Paper>
  );
}

// ─── main component ──────────────────────────────────────────────────────────

export default function OrderCard() {
  const { id } = useParams<{ id?: string }>();
  const isNew = id === undefined;
  const navigate = useNavigate();
  const qc = useQueryClient();

  // ── active tab state (for lazy loading history) ──
  const [activeTab, setActiveTab] = useState<string | null>("order");

  // ── unlock modal state ──
  const [unlockModalOpen, setUnlockModalOpen] = useState(false);
  const [unlockReason, setUnlockReason] = useState("");
  const [unlockReasonError, setUnlockReasonError] = useState("");
  const [sendDialog, setSendDialog] = useState<{ open: boolean; kind: "customer" | "carrier" }>({
    open: false,
    kind: "customer",
  });

  // ── negative-commission confirm modal (#95: вместо нативного window.confirm) ──
  const [negCommissionOpen, setNegCommissionOpen] = useState(false);

  // ── client contracts state ──
  const [clientContracts, setClientContracts] = useState<Contract[]>([]);
  const [carrierContracts, setCarrierContracts] = useState<Contract[]>([]);

  // ── load static selects ──
  const { data: companiesData } = useQuery({
    queryKey: qk.companies({ page: 1, page_size: 200 }),
    queryFn: async () =>
      (await api.get<CompaniesListResponse>("/api/v1/companies?page=1&page_size=200")).data,
    staleTime: 5 * 60 * 1000,
  });

  const { data: usersData } = useQuery({
    queryKey: qk.users({ page: 1, page_size: 200 }),
    queryFn: async () =>
      (await api.get<UsersListResponse>("/api/v1/users?page=1&page_size=200")).data,
    staleTime: 5 * 60 * 1000,
  });

  // Для селекта в заявке берём только одобренных контрагентов — заявку с
  // pending заказчиком/перевозчиком создавать нельзя, такого требует
  // бизнес-процесс (модерация — перед использованием).
  const { data: clientsData } = useQuery({
    queryKey: qk.clients({ page: 1, page_size: 200, status: "approved" }),
    queryFn: async () =>
      (await api.get<ClientsListResponse>("/api/v1/clients?page=1&page_size=200&status=approved")).data,
    staleTime: 5 * 60 * 1000,
  });

  const { data: carriersData } = useQuery({
    queryKey: qk.carriers({ page: 1, page_size: 200, status: "approved" }),
    queryFn: async () =>
      (await api.get<CarriersListResponse>("/api/v1/carriers?page=1&page_size=200&status=approved")).data,
    staleTime: 5 * 60 * 1000,
  });

  const { data: vehiclesData } = useQuery({
    queryKey: qk.vehicles({ page: 1, page_size: 200 }),
    queryFn: async () =>
      (await api.get<VehiclesListResponse>("/api/v1/vehicles?page=1&page_size=200")).data,
    staleTime: 5 * 60 * 1000,
  });

  const { data: driversData } = useQuery({
    queryKey: qk.drivers({ page: 1, page_size: 200 }),
    queryFn: async () =>
      (await api.get<{ items: { id: string; full_name: string }[]; total: number }>(
        "/api/v1/drivers?page=1&page_size=200"
      )).data,
    staleTime: 5 * 60 * 1000,
  });

  const { data: statusesData } = useQuery({
    queryKey: qk.orderStatuses(),
    queryFn: async () => (await api.get<OrderStatus[]>("/api/v1/order-statuses")).data,
    staleTime: 5 * 60 * 1000,
  });

  // ── load existing order (edit mode) ──
  const { data: order, isLoading: orderLoading } = useQuery({
    queryKey: qk.order(id!),
    queryFn: async () => (await api.get<Order>(`/api/v1/orders/${id}`)).data,
    enabled: !isNew,
    staleTime: 0,
  });

  // ── form ──
  const form = useForm<FormValues>({
    initialValues: {
      company_id: "",
      order_type: "",
      document_date: today(),
      number_external: "",
      title: "Заявка на осуществление перевозки",
      responsible_manager_id: "",
      client_id: "",
      client_contract_id: "",
      client_amount: "",
      client_vat_rate: 0,
      carrier_id: "",
      carrier_contract_id: "",
      carrier_amount: "",
      carrier_vat_rate: 0,
      vehicle_id: "",
      vehicle_text: "",
      driver_id: "",
      driver_text: "",
      distance_km: "",
      notes: "",
      extra_conditions: "",
      additional_terms_client: "",
      additional_terms_carrier: "",
    },
    validate: {
      company_id: (v) => (v ? null : "Своя фирма обязательна"),
    },
  });

  // ── populate form when order loaded ──
  useEffect(() => {
    if (!order) return;
    form.setValues({
      company_id: order.company_id ?? "",
      order_type: order.order_type ?? "",
      document_date: parseDate(order.document_date),
      number_external: order.number_external ?? "",
      title: order.title ?? "",
      responsible_manager_id: order.responsible_manager_id ?? "",
      client_id: order.client_id ?? "",
      client_contract_id: order.client_contract_id ?? "",
      client_amount: order.client_amount ?? "",
      client_vat_rate: order.client_vat_rate ?? 0,
      carrier_id: order.carrier_id ?? "",
      carrier_contract_id: order.carrier_contract_id ?? "",
      carrier_amount: order.carrier_amount ?? "",
      carrier_vat_rate: order.carrier_vat_rate ?? 0,
      vehicle_id: order.vehicle_id ?? "",
      vehicle_text: order.vehicle_text ?? "",
      driver_id: order.driver_id ?? "",
      driver_text: order.driver_text ?? "",
      distance_km: order.distance_km ?? "",
      notes: order.notes ?? "",
      extra_conditions: order.extra_conditions ?? "",
      additional_terms_client: order.additional_terms_client ?? "",
      additional_terms_carrier: order.additional_terms_carrier ?? "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order]);

  // ── fetch client contracts when client_id changes ──
  useEffect(() => {
    const cid = form.values.client_id;
    if (!cid) {
      setClientContracts([]);
      form.setFieldValue("client_contract_id", "");
      return;
    }
    api
      .get<{ items: Contract[] }>(`/api/v1/clients/${cid}/contracts`)
      .then((r) => {
        const contracts = (r.data.items ?? r.data) as Contract[];
        setClientContracts(contracts);
        // auto-select if only one — НО только среди договоров с выбранной нашей
        // фирмой (иначе подставим невидимый в выпадашке договор другой фирмы и
        // сохранение упадёт «договор от другой фирмы»).
        const companyId = form.values.company_id;
        const matching = contracts.filter((c) => companyId && c.company_id === companyId);
        if (matching.length === 1 && !form.values.client_contract_id) {
          form.setFieldValue("client_contract_id", matching[0].id);
          form.setFieldValue("client_vat_rate", matching[0].with_vat ? 22 : 0);
        }
      })
      .catch(() => setClientContracts([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.client_id]);

  // ── fetch carrier contracts when carrier_id changes ──
  useEffect(() => {
    const cid = form.values.carrier_id;
    if (!cid) {
      setCarrierContracts([]);
      form.setFieldValue("carrier_contract_id", "");
      return;
    }
    api
      .get<{ items: Contract[] }>(`/api/v1/carriers/${cid}/contracts`)
      .then((r) => {
        const contracts = (r.data.items ?? r.data) as Contract[];
        setCarrierContracts(contracts);
        // auto-select только среди договоров с выбранной нашей фирмой (см. коммент
        // в эффекте по заказчику выше).
        const companyId = form.values.company_id;
        const matching = contracts.filter((c) => companyId && c.company_id === companyId);
        if (matching.length === 1 && !form.values.carrier_contract_id) {
          form.setFieldValue("carrier_contract_id", matching[0].id);
          form.setFieldValue("carrier_vat_rate", matching[0].with_vat ? 22 : 0);
        }
      })
      .catch(() => setCarrierContracts([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.carrier_id]);

  // ── auto-fill vat rate on contract select ──
  function onClientContractChange(contractId: string | null) {
    form.setFieldValue("client_contract_id", contractId ?? "");
    const contract = clientContracts.find((c) => c.id === contractId);
    if (contract) {
      form.setFieldValue("client_vat_rate", contract.with_vat ? 22 : 0);
    }
  }

  function onCarrierContractChange(contractId: string | null) {
    form.setFieldValue("carrier_contract_id", contractId ?? "");
    const contract = carrierContracts.find((c) => c.id === contractId);
    if (contract) {
      form.setFieldValue("carrier_vat_rate", contract.with_vat ? 22 : 0);
    }
  }

  // ── mutations ──
  const createMutation = useMutation({
    mutationFn: (body: OrderCreateRequest) =>
      api.post<Order>("/api/v1/orders", body),
    onSuccess: (resp) => {
      notifications.show({
        color: "green",
        title: "Заявка создана",
        message: `№ ${resp.data.number_internal}`,
      });
      qc.invalidateQueries({ queryKey: ["orders"] });
      navigate(`/orders/${resp.data.id}`);
    },
    onError: (err) => {
      notifications.show({
        color: "red",
        title: "Ошибка",
        message: formatAPIError(err),
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (body: OrderUpdateRequest) =>
      api.patch<Order>(`/api/v1/orders/${id}`, body),
    onSuccess: () => {
      notifications.show({ color: "green", title: "Сохранено", message: "Заявка обновлена" });
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: qk.order(id!) });
    },
    onError: (err: unknown) => {
      const ax = err as { response?: { data?: { error?: { code?: string } } } };
      if (ax?.response?.data?.error?.code === "ORDER_LOCKED") {
        notifications.show({
          color: "red",
          title: "Заблокировано",
          message: "Заявка заблокирована после выставления счёта",
        });
      } else {
        notifications.show({
          color: "red",
          title: "Ошибка",
          message: formatAPIError(err),
        });
      }
    },
  });

  // ── status change mutation ──
  const statusMutation = useMutation({
    mutationFn: (status_code: string) =>
      api.post(`/api/v1/orders/${id}/status`, { status_code }),
    onSuccess: () => {
      notifications.show({ color: "teal", title: "Статус изменён", message: "" });
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: qk.order(id!) });
      qc.invalidateQueries({ queryKey: qk.orderAudit(id!) });
    },
    onError: (err) => {
      notifications.show({ color: "red", title: "Ошибка", message: formatAPIError(err) });
    },
  });

  // ── unlock mutation ──
  const unlockMutation = useMutation({
    mutationFn: (reason: string) =>
      api.post(`/api/v1/orders/${id}/unlock`, { reason }),
    onSuccess: () => {
      notifications.show({ color: "green", title: "Блокировка снята", message: "" });
      setUnlockModalOpen(false);
      setUnlockReason("");
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: qk.order(id!) });
      qc.invalidateQueries({ queryKey: qk.orderAudit(id!) });
    },
    onError: (err) => {
      notifications.show({ color: "red", title: "Ошибка", message: formatAPIError(err) });
    },
  });

  // Полная копия заявки. Бэк возвращает новый Order с новым id и новым
  // number_internal. Сразу переходим в карточку копии — пользователь
  // редактирует то что нужно поменять (перевозчика, ставки, фирму) и сохраняет.
  // Скачивание печатной формы. window.open не подходит: наша авторизация —
  // Bearer-header из JS, «голый» браузерный GET уйдёт без него и получит 401.
  // Качаем через axios (blob) и отдаём пользователю через createObjectURL.
  async function downloadPrint(kind: "customer" | "carrier") {
    try {
      const r = await api.get(`/api/v1/orders/${id}/print?kind=${kind}`, {
        responseType: "blob",
      });
      const cd: string = r.headers["content-disposition"] ?? "";
      const m = /filename\*=UTF-8''([^;]+)/i.exec(cd);
      const filename = m
        ? decodeURIComponent(m[1])
        : `order_${order?.number_internal ?? id}_${kind}.docx`;
      const url = URL.createObjectURL(r.data as Blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      notifications.show({ color: "red", title: "Не удалось сформировать файл", message: formatAPIError(err) });
    }
  }

  const copyMutation = useMutation({
    mutationFn: () =>
      api.post<Order>(`/api/v1/orders/${id}/copy`),
    onSuccess: (resp) => {
      notifications.show({
        color: "green",
        title: "Заявка скопирована",
        message: `№ ${resp.data.number_internal}`,
      });
      qc.invalidateQueries({ queryKey: ["orders"] });
      navigate(`/orders/${resp.data.id}`);
    },
    onError: (err) => {
      notifications.show({ color: "red", title: "Ошибка", message: formatAPIError(err) });
    },
  });

  // ── build request body ──
  // Для UPDATE FK-поля шлём явно "" когда пустые, чтобы бэк увидел очистку
  // (через handler-логику {"vehicle_id":""} → in.ClearVehicleID=true → SET NULL).
  // Для CREATE undefined нормально — бэк просто не записывает поле.
  function buildBody(): OrderCreateRequest {
    const v = form.values;
    // FK-helper: "" в update, undefined в create.
    const fk = (val: string) => (isNew ? (val || undefined) : val);
    return {
      company_id: v.company_id,
      order_type: v.order_type || undefined,
      document_date: v.document_date ? dayjs(v.document_date).format("YYYY-MM-DD") : undefined,
      number_external: v.number_external || undefined,
      title: v.title || undefined,
      responsible_manager_id: v.responsible_manager_id || undefined,
      client_id: fk(v.client_id),
      client_contract_id: fk(v.client_contract_id),
      client_amount: v.client_amount !== "" ? Number(v.client_amount) : undefined,
      client_vat_rate: v.client_vat_rate !== "" ? Number(v.client_vat_rate) : undefined,
      carrier_id: fk(v.carrier_id),
      carrier_contract_id: fk(v.carrier_contract_id),
      carrier_amount: v.carrier_amount !== "" ? Number(v.carrier_amount) : undefined,
      carrier_vat_rate: v.carrier_vat_rate !== "" ? Number(v.carrier_vat_rate) : undefined,
      vehicle_id: fk(v.vehicle_id),
      vehicle_text: v.vehicle_text || undefined,
      driver_id: fk(v.driver_id),
      driver_text: v.driver_text || undefined,
      distance_km: v.distance_km !== "" ? Number(v.distance_km) : undefined,
      notes: v.notes || undefined,
      extra_conditions: v.extra_conditions || undefined,
      // Для UPDATE отправляем "" явно (пустое — валидное значение).
      // Для CREATE тоже не страшно: бэк NOT NULL DEFAULT ''.
      additional_terms_client: v.additional_terms_client ?? "",
      additional_terms_carrier: v.additional_terms_carrier ?? "",
    };
  }

  function doSave() {
    const body = buildBody();
    if (isNew) {
      createMutation.mutate(body);
    } else {
      updateMutation.mutate(body);
    }
  }

  function handleSubmit() {
    const result = form.validate();
    if (result.hasErrors) return;

    // Предупреждение при отрицательной комиссии (заказчик жаловался #89).
    // НЕ блокировка — такие заявки правомерны (внутренние перевозки, штрафы,
    // спецслучаи), но пользователь должен явно подтвердить. Раньше тут был
    // нативный window.confirm — заказчик просил нормальную модалку под стиль
    // сайта (#95): показываем стилизованный Modal ниже.
    if (commission < 0) {
      setNegCommissionOpen(true);
      return;
    }
    doSave();
  }

  function handleUnlockSubmit() {
    if (!unlockReason.trim()) {
      setUnlockReasonError("Укажите причину снятия блокировки");
      return;
    }
    setUnlockReasonError("");
    unlockMutation.mutate(unlockReason.trim());
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;

  // ── derived ──
  const clientAmt = Number(form.values.client_amount) || 0;
  const clientVat = Number(form.values.client_vat_rate) || 0;
  const carrierAmt = Number(form.values.carrier_amount) || 0;
  const carrierVat = Number(form.values.carrier_vat_rate) || 0;
  // Та же формула что и в backend/internal/orders/commission.go ComputeCommission.
  // Если заказчик с НДС — вычитаем НДС у заказчика (и у перевозчика если он тоже с НДС).
  // Если заказчик без НДС — простая разница, НДС перевозчика игнорируем.
  const commission = clientVat > 0
    ? clientAmt / (1 + clientVat / 100) -
      (carrierVat > 0 ? carrierAmt / (1 + carrierVat / 100) : carrierAmt)
    : clientAmt - carrierAmt;

  // ── select option lists ──
  const companyOptions =
    companiesData?.items.map((c) => ({ value: c.id, label: c.name })) ?? [];

  const userOptions =
    usersData?.items
      .filter((u) => u.active)
      .map((u) => ({ value: u.id, label: u.full_name })) ?? [];

  // Аналог carrierOptions: рейтинг АТИ в подписи + disabled если ограничен.
  const clientOptions =
    clientsData?.items.map((c) => {
      const ratingLabel =
        c.ati_rating != null ? ` ★${c.ati_rating.toFixed(1)}` : "";
      const restrictedLabel = c.work_restricted ? " (ограничен)" : "";
      return {
        value: c.id,
        label: `${c.name}${ratingLabel}${restrictedLabel}`,
        disabled: c.work_restricted,
      };
    }) ?? [];

  // Показываем рейтинг АТИ в названии и блокируем выбор work_restricted
  // (защита от дурака — заказчик жаловался что можно ткнуть перевозчика
  // с низким рейтингом). disabled=true в Mantine Select не даёт выбрать.
  const carrierOptions =
    carriersData?.items.map((c) => {
      const ratingLabel =
        c.ati_rating != null
          ? ` ★${c.ati_rating.toFixed(1)}`
          : "";
      const restrictedLabel = c.work_restricted ? " (ограничен)" : "";
      return {
        value: c.id,
        label: `${c.name}${ratingLabel}${restrictedLabel}`,
        disabled: c.work_restricted,
      };
    }) ?? [];

  function contractLabel(c: Contract): string {
    return `${c.number}${c.company_name ? ` (${c.company_name}` : ""}${c.with_vat ? ", НДС" : ""}${c.company_name ? ")" : ""}`;
  }

  // Фильтрация по своей фирме: показываем только договоры, заключённые
  // с выбранной нашей фирмой (нельзя смешивать — заявку фирмы А с договором
  // фирмы Б). Без выбранной фирмы — никаких вариантов (пусть сначала выберет).
  const activeCompanyId = form.values.company_id;
  const clientContractOptions = clientContracts
    .filter((c) => activeCompanyId && c.company_id === activeCompanyId)
    .map((c) => ({ value: c.id, label: contractLabel(c) }));

  const carrierContractOptions = carrierContracts
    .filter((c) => activeCompanyId && c.company_id === activeCompanyId)
    .map((c) => ({ value: c.id, label: contractLabel(c) }));

  const vehicleOptions =
    vehiclesData?.items.map((v) => ({
      value: v.id,
      label: [v.brand, v.license_plate].filter(Boolean).join(" — "),
    })) ?? [];

  const driverOptions =
    driversData?.items.map((d) => ({ value: d.id, label: d.full_name })) ?? [];

  // ── status badge ──
  const status = statusesData?.find((s) => s.id === order?.status_id);

  // ── loading state ──
  if (!isNew && orderLoading) {
    return (
      <Stack align="center" pt="xl">
        <Loader />
        <Text c="dimmed">Загрузка заявки…</Text>
      </Stack>
    );
  }

  const isLocked = order?.locked ?? false;

  return (
    <Stack gap="md">
      {/* ── header ── */}
      <Group justify="space-between" align="flex-start">
        <Stack gap={4}>
          <Group gap="sm" align="center">
            <Title order={3}>
              {isNew ? "Новая заявка" : `Заявка № ${order?.number_internal}`}
            </Title>
            {status && (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  backgroundColor: status.color,
                  color: "#fff",
                  borderRadius: 999,
                  padding: "2px 10px",
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {status.name}
              </span>
            )}
            {isLocked && (
              <Badge color="orange" variant="light" leftSection={<IconLock size={12} />}>
                Заблокирована (счёт выставлен)
              </Badge>
            )}
          </Group>
          {!isNew && order?.number_external && (
            <Text size="sm" c="dimmed">
              Вх. № {order.number_external}
            </Text>
          )}
        </Stack>
        <Group>
          {/* ── Status change menu (edit mode only) ── */}
          {!isNew && (
            <Can perm="orders:status">
              <Menu shadow="md" width={240}>
                <Menu.Target>
                  <Button
                    variant="light"
                    color="teal"
                    rightSection={<IconChevronDown size={14} />}
                    loading={statusMutation.isPending}
                  >
                    Сменить статус
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Label>Выберите новый статус</Menu.Label>
                  {(statusesData ?? [])
                    .slice()
                    .sort((a, b) => a.sort_order - b.sort_order)
                    .map((s) => (
                      <Menu.Item
                        key={s.id}
                        disabled={s.id === order?.status_id}
                        leftSection={
                          <span
                            style={{
                              display: "inline-block",
                              width: 10,
                              height: 10,
                              borderRadius: "50%",
                              backgroundColor: s.color,
                            }}
                          />
                        }
                        onClick={() => statusMutation.mutate(s.code)}
                      >
                        {s.name}
                        {s.is_blocking && (
                          <Text span size="xs" c="dimmed" ml={4}>(блокирует)</Text>
                        )}
                      </Menu.Item>
                    ))}
                </Menu.Dropdown>
              </Menu>
            </Can>
          )}

          {/* ── Unlock button (edit mode + locked only) ── */}
          {!isNew && isLocked && (
            <Can perm="orders:unlock">
              <Button
                variant="light"
                color="orange"
                leftSection={<IconLockOpen size={16} />}
                onClick={() => { setUnlockReason(""); setUnlockReasonError(""); setUnlockModalOpen(true); }}
              >
                Снять блокировку
              </Button>
            </Can>
          )}

          <Button
            variant="default"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate("/orders")}
          >
            К журналу
          </Button>
          {!isNew && (
            <Menu shadow="md" width={220}>
              <Menu.Target>
                <Button
                  variant="light"
                  color="grape"
                  leftSection={<IconPrinter size={16} />}
                  rightSection={<IconChevronDown size={14} />}
                >
                  Печать
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>Выбор шаблона</Menu.Label>
                <Menu.Item
                  leftSection={<IconFileTypeDocx size={14} />}
                  onClick={() => downloadPrint("customer")}
                >
                  Заявка заказчику (docx)
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconFileTypeDocx size={14} />}
                  onClick={() => downloadPrint("carrier")}
                >
                  Заявка перевозчику (docx)
                </Menu.Item>
                <Menu.Divider />
                <Menu.Label>Отправить на email</Menu.Label>
                <Menu.Item
                  leftSection={<IconMail size={14} />}
                  onClick={() => setSendDialog({ open: true, kind: "customer" })}
                >
                  Отправить заказчику…
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconMail size={14} />}
                  onClick={() => setSendDialog({ open: true, kind: "carrier" })}
                >
                  Отправить перевозчику…
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          )}
          {!isNew && (
            <Can perm="orders:write">
              <Button
                variant="light"
                color="blue"
                leftSection={<IconCopy size={16} />}
                loading={copyMutation.isPending}
                onClick={() => copyMutation.mutate()}
              >
                Скопировать
              </Button>
            </Can>
          )}
          <Tooltip
            label="Заявка заблокирована, редактирование запрещено"
            disabled={!isLocked}
          >
            <Button
              leftSection={<IconDeviceFloppy size={16} />}
              loading={isSaving}
              onClick={handleSubmit}
              disabled={isLocked}
            >
              Сохранить
            </Button>
          </Tooltip>
        </Group>
      </Group>

      {isLocked && (
        <Alert color="orange" title="Заявка заблокирована" icon={<IconLock size={16} />}>
          Редактирование ограничено: заявка в статусе «{status?.name ?? "Счёт выставлен"}». Для
          разблокировки обратитесь к директору или бухгалтеру.
        </Alert>
      )}

      {/* ── tabs ── */}
      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List>
          <Tabs.Tab value="order">Заявка</Tabs.Tab>
          <Tabs.Tab value="loading">Погрузка/Разгрузка</Tabs.Tab>
          <Tabs.Tab value="cargo">Груз</Tabs.Tab>
          <Tabs.Tab value="services">Услуги</Tabs.Tab>
          <Tabs.Tab value="payments">Расчёты</Tabs.Tab>
          <Tabs.Tab value="documents">Документы</Tabs.Tab>
          <Tabs.Tab value="history">История</Tabs.Tab>
        </Tabs.List>

        {/* ─── Заявка tab ─── */}
        <Tabs.Panel value="order" pt="md">
          <Stack gap="md">
            {/* ── Основное ── */}
            <Paper p="md" withBorder>
              <Stack gap="sm">
                <Text fw={600} size="sm">
                  Основное
                </Text>
                <Divider />
                <Group grow align="flex-start">
                  <Select
                    label="Своя фирма"
                    placeholder="Выберите фирму"
                    required
                    searchable
                    data={companyOptions}
                    {...form.getInputProps("company_id")}
                    onChange={(v) => {
                      const prev = form.values.company_id;
                      form.setFieldValue("company_id", v ?? "");
                      // Договоры привязаны к конкретной своей фирме. Меняем
                      // фирму — обнуляем оба договора и НДС, чтобы пользователь
                      // не оставил «договор от другой фирмы».
                      if (prev && prev !== (v ?? "")) {
                        form.setFieldValue("client_contract_id", "");
                        form.setFieldValue("carrier_contract_id", "");
                        form.setFieldValue("client_vat_rate", 0);
                        form.setFieldValue("carrier_vat_rate", 0);
                      }
                    }}
                    disabled={isLocked}
                  />
                  <Select
                    label="Тип заявки"
                    placeholder="Не указан"
                    data={[
                      { value: "domestic", label: "Внутренняя" },
                      { value: "international", label: "Международная" },
                    ]}
                    clearable
                    {...form.getInputProps("order_type")}
                    onChange={(v) => form.setFieldValue("order_type", v ?? "")}
                    disabled={isLocked}
                  />
                  <DateInput
                    label="Дата документа"
                    placeholder="дд.мм.гггг"
                    valueFormat="DD.MM.YYYY"
                    {...form.getInputProps("document_date")}
                    disabled={isLocked}
                  />
                </Group>
                <Group grow align="flex-start">
                  <TextInput
                    label="Вх. №"
                    placeholder="Номер от заказчика"
                    {...form.getInputProps("number_external")}
                    disabled={isLocked}
                  />
                  <TextInput
                    label="Заголовок"
                    placeholder="Заявка на осуществление перевозки"
                    {...form.getInputProps("title")}
                    disabled={isLocked}
                  />
                  <Select
                    label="Ответственный менеджер"
                    placeholder="Выберите менеджера"
                    searchable
                    clearable
                    data={userOptions}
                    {...form.getInputProps("responsible_manager_id")}
                    onChange={(v) => form.setFieldValue("responsible_manager_id", v ?? "")}
                    disabled={isLocked}
                  />
                </Group>
              </Stack>
            </Paper>

            {/* ── Заказчик ── */}
            <Paper p="md" withBorder>
              <Stack gap="sm">
                <Text fw={600} size="sm">
                  Заказчик
                </Text>
                <Divider />
                <Group grow align="flex-start">
                  <Select
                    label="Заказчик"
                    placeholder="Выберите заказчика"
                    searchable
                    clearable
                    data={clientOptions}
                    value={form.values.client_id || null}
                    onChange={(v) => {
                      form.setFieldValue("client_id", v ?? "");
                      form.setFieldValue("client_contract_id", "");
                    }}
                    disabled={isLocked}
                  />
                  <Select
                    label="Договор с заказчиком"
                    placeholder={
                      form.values.client_id
                        ? clientContracts.length === 0
                          ? "Нет договоров"
                          : "Выберите договор"
                        : "Сначала выберите заказчика"
                    }
                    searchable
                    clearable
                    data={clientContractOptions}
                    value={form.values.client_contract_id || null}
                    onChange={onClientContractChange}
                    disabled={isLocked || !form.values.client_id}
                  />
                </Group>
                <Group grow align="flex-start">
                  <NumberInput
                    label="Сумма заказчика, ₽"
                    placeholder="0.00"
                    min={0}
                    decimalScale={2}
                    {...form.getInputProps("client_amount")}
                    disabled={isLocked}
                  />
                  <NumberInput
                    label="НДС заказчика, %"
                    placeholder="0"
                    min={0}
                    max={100}
                    {...form.getInputProps("client_vat_rate")}
                    disabled={isLocked}
                  />
                </Group>
              </Stack>
            </Paper>

            {/* ── Перевозчик ── */}
            <Paper p="md" withBorder>
              <Stack gap="sm">
                <Text fw={600} size="sm">
                  Перевозчик
                </Text>
                <Divider />
                <Group grow align="flex-start">
                  <Select
                    label="Перевозчик"
                    placeholder="Выберите перевозчика"
                    searchable
                    clearable
                    data={carrierOptions}
                    value={form.values.carrier_id || null}
                    onChange={(v) => {
                      const prev = form.values.carrier_id;
                      form.setFieldValue("carrier_id", v ?? "");
                      form.setFieldValue("carrier_contract_id", "");
                      // Машина и водитель привязаны к перевозчику. Если меняем
                      // перевозчика — старые ТС/водитель не принадлежат новому,
                      // их надо сбросить. Сбрасываем только если действительно
                      // была смена (а не при первой установке carrier_id из загрузки).
                      if (prev && prev !== (v ?? "")) {
                        form.setFieldValue("vehicle_id", "");
                        form.setFieldValue("driver_id", "");
                        form.setFieldValue("vehicle_text", "");
                        form.setFieldValue("driver_text", "");
                      }
                    }}
                    disabled={isLocked}
                  />
                  <Select
                    label="Договор с перевозчиком"
                    placeholder={
                      form.values.carrier_id
                        ? carrierContracts.length === 0
                          ? "Нет договоров"
                          : "Выберите договор"
                        : "Сначала выберите перевозчика"
                    }
                    searchable
                    clearable
                    data={carrierContractOptions}
                    value={form.values.carrier_contract_id || null}
                    onChange={onCarrierContractChange}
                    disabled={isLocked || !form.values.carrier_id}
                  />
                </Group>
                <Group grow align="flex-start">
                  <NumberInput
                    label="Сумма перевозчика, ₽"
                    placeholder="0.00"
                    min={0}
                    decimalScale={2}
                    {...form.getInputProps("carrier_amount")}
                    disabled={isLocked}
                  />
                  <NumberInput
                    label="НДС перевозчика, %"
                    placeholder="0"
                    min={0}
                    max={100}
                    {...form.getInputProps("carrier_vat_rate")}
                    disabled={isLocked}
                  />
                </Group>
              </Stack>
            </Paper>

            {/* ── Комиссия ── */}
            <Paper p="md" withBorder>
              <Group justify="space-between" align="center">
                <Text fw={600} size="sm">
                  Комиссия
                </Text>
                <Text
                  fw={700}
                  size="lg"
                  c={commission >= 0 ? "green" : "red"}
                >
                  {formatMoney(commission)}
                </Text>
              </Group>
              {(clientAmt > 0 || carrierAmt > 0) && (
                <Text size="xs" c="dimmed" mt={4}>
                  {formatMoney(clientAmt)} (заказчик) − {formatMoney(carrierAmt)} (перевозчик)
                </Text>
              )}
            </Paper>

            {/* ── Транспорт и водитель ── */}
            <Paper p="md" withBorder>
              <Stack gap="sm">
                <Text fw={600} size="sm">
                  Транспорт и водитель
                </Text>
                <Divider />
                <Text size="xs" c="dimmed">
                  Если транспорта/водителя нет в справочнике — впишите текстом
                </Text>
                <Group grow align="flex-start">
                  <Select
                    label="Транспорт (из справочника)"
                    placeholder="Выберите ТС"
                    searchable
                    clearable
                    data={vehicleOptions}
                    value={form.values.vehicle_id || null}
                    onChange={(v) => form.setFieldValue("vehicle_id", v ?? "")}
                    disabled={isLocked}
                  />
                  <TextInput
                    label="Транспорт (текстом)"
                    placeholder="Марка, госномер..."
                    {...form.getInputProps("vehicle_text")}
                    disabled={isLocked}
                  />
                </Group>
                <Group grow align="flex-start">
                  <Select
                    label="Водитель (из справочника)"
                    placeholder="Выберите водителя"
                    searchable
                    clearable
                    data={driverOptions}
                    value={form.values.driver_id || null}
                    onChange={(v) => form.setFieldValue("driver_id", v ?? "")}
                    disabled={isLocked}
                  />
                  <TextInput
                    label="Водитель (текстом)"
                    placeholder="ФИО водителя..."
                    {...form.getInputProps("driver_text")}
                    disabled={isLocked}
                  />
                </Group>
                <Group grow align="flex-start">
                  <NumberInput
                    label="Дистанция, км"
                    placeholder="0"
                    min={0}
                    style={{ maxWidth: 200 }}
                    {...form.getInputProps("distance_km")}
                    disabled={isLocked}
                  />
                  <div /> {/* spacer */}
                </Group>
              </Stack>
            </Paper>

            {/* ── Дополнительно ── */}
            <Paper p="md" withBorder>
              <Stack gap="sm">
                <Text fw={600} size="sm">
                  Дополнительно
                </Text>
                <Divider />
                <Textarea
                  label="Примечания"
                  placeholder="Примечания к заявке..."
                  rows={3}
                  autosize
                  minRows={2}
                  {...form.getInputProps("notes")}
                  disabled={isLocked}
                />
                <Textarea
                  label="Доп. условия (общее, идут в текст заявки)"
                  placeholder="Общие доп. условия перевозки..."
                  rows={2}
                  autosize
                  minRows={2}
                  {...form.getInputProps("extra_conditions")}
                  disabled={isLocked}
                />
                {/* ТЗ 01.07.2026 п.7: два отдельных свободных текстовых поля.
                    Идут в §5–7 печатной формы. Если пусто — берётся дефолт своей фирмы. */}
                <Textarea
                  label="Доп. условия для Заказчика (для заявки заказчику)"
                  placeholder="Условия, штрафные санкции, текст договора — идут в PDF заказчику..."
                  autosize
                  minRows={4}
                  {...form.getInputProps("additional_terms_client")}
                  disabled={isLocked}
                />
                <Textarea
                  label="Доп. условия для Перевозчика (для заявки перевозчику)"
                  placeholder="Условия, штрафные санкции — идут в PDF перевозчику..."
                  autosize
                  minRows={4}
                  {...form.getInputProps("additional_terms_carrier")}
                  disabled={isLocked}
                />
              </Stack>
            </Paper>
          </Stack>
        </Tabs.Panel>

        {/* ─── Погрузка/Разгрузка tab ─── */}
        <Tabs.Panel value="loading" pt="md">
          <LoadingPointsTab orderId={isNew ? undefined : id} />
        </Tabs.Panel>

        {/* ─── Груз tab ─── */}
        <Tabs.Panel value="cargo" pt="md">
          <CargoTab orderId={isNew ? undefined : id} />
        </Tabs.Panel>

        {/* ─── Услуги tab ─── */}
        <Tabs.Panel value="services" pt="md">
          <ServicesTab orderId={isNew ? undefined : id} />
        </Tabs.Panel>

        {/* ─── Расчёты tab (финансовый блок) ─── */}
        <Tabs.Panel value="payments" pt="md">
          {isNew || !order ? (
            <Paper p="xl" withBorder>
              <Text c="dimmed" ta="center" size="sm">
                Расчёты доступны после сохранения заявки.
              </Text>
            </Paper>
          ) : (
            <PaymentsTab order={order} isLocked={isLocked} />
          )}
        </Tabs.Panel>

        {/* ─── Документы tab (входящие/исходящие + Почта России) ─── */}
        <Tabs.Panel value="documents" pt="md">
          {isNew || !order ? (
            <Paper p="xl" withBorder>
              <Text c="dimmed" ta="center" size="sm">
                Документы доступны после сохранения заявки.
              </Text>
            </Paper>
          ) : (
            <DocumentsTab order={order} isLocked={isLocked} />
          )}
        </Tabs.Panel>

        {/* ─── История tab ─── */}
        <Tabs.Panel value="history" pt="md">
          {isNew ? (
            <Paper p="xl" withBorder>
              <Text c="dimmed" ta="center" size="sm">
                История доступна после сохранения заявки.
              </Text>
            </Paper>
          ) : (
            <AuditTimeline orderId={id!} active={activeTab === "history"} />
          )}
        </Tabs.Panel>
      </Tabs>

      {/* ─── Unlock modal ─── */}
      <Modal
        opened={unlockModalOpen}
        onClose={() => setUnlockModalOpen(false)}
        title="Снять блокировку заявки"
        size="md"
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Укажите причину снятия блокировки. Это действие будет записано в историю заявки.
          </Text>
          <Textarea
            label="Причина снятия блокировки"
            placeholder="Например: исправление ошибки в счёте..."
            required
            minRows={3}
            value={unlockReason}
            onChange={(e) => {
              setUnlockReason(e.currentTarget.value);
              if (e.currentTarget.value.trim()) setUnlockReasonError("");
            }}
            error={unlockReasonError}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setUnlockModalOpen(false)}>
              Отмена
            </Button>
            <Button
              color="orange"
              leftSection={<IconLockOpen size={16} />}
              loading={unlockMutation.isPending}
              onClick={handleUnlockSubmit}
            >
              Снять блокировку
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ─── Negative commission confirm modal ─── */}
      <Modal
        opened={negCommissionOpen}
        onClose={() => setNegCommissionOpen(false)}
        title="Отрицательная комиссия"
        size="md"
        centered
      >
        <Stack gap="md">
          <Alert color="orange" icon={<IconLock size={16} />}>
            Комиссия по этой заявке отрицательная: <b>{formatMoney(commission)}</b>.
          </Alert>
          <Text size="sm" c="dimmed">
            Это возможно (внутренняя перевозка, штраф и т.п.), но обычно — ошибка
            во вводе ставок. Сохранить заявку с такой комиссией?
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setNegCommissionOpen(false)}>
              Отмена
            </Button>
            <Button
              color="orange"
              leftSection={<IconDeviceFloppy size={16} />}
              loading={isSaving}
              onClick={() => {
                setNegCommissionOpen(false);
                doSave();
              }}
            >
              Сохранить всё равно
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ─── Send print dialog ─── */}
      {!isNew && order && (
        <SendPrintDialog
          order={order}
          kind={sendDialog.kind}
          opened={sendDialog.open}
          onClose={() => setSendDialog((s) => ({ ...s, open: false }))}
        />
      )}
    </Stack>
  );
}
